import {
  AlertTriangle,
  Users,
  Radio,
  BarChart3,
  Heart,
  Brain,
  CheckCircle2,
  Scale,
  MapPin,
  ArrowRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/* ------------------------------------------------------------- */
/*  Reusable section header                                      */
/* ------------------------------------------------------------- */
function SectionEyebrow({ children, tone = "blue" }) {
  const color =
    tone === "purple"
      ? "text-[#7E20FF]"
      : tone === "warning"
      ? "text-[#D94A0A]"
      : "text-[#208BFF]";
  return (
    <span
      className={`text-xs font-semibold tracking-[0.22em] uppercase ${color}`}
    >
      {children}
    </span>
  );
}

/* ============================================================= */
/*  2 — Problem                                                  */
/* ============================================================= */
const PAINS = [
  "Sua base está espalhada em dezenas de grupos de WhatsApp.",
  "Você não sabe exatamente quem está fazendo algo pela campanha.",
  "Fica difícil medir o impacto das ações e reagir rápido às narrativas que surgem.",
  "Decisões importantes são tomadas no escuro, sem dado de campo.",
];

export function ProblemSection() {
  return (
    <section
      id="problema"
      data-testid="problem-section"
      className="relative py-24 sm:py-32 border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <SectionEyebrow tone="warning">O problema</SectionEyebrow>
          <h2
            data-testid="problem-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Campanhas não perdem só por falta de verba.
            <span className="block text-[#51565F] mt-2 font-medium">
              Perdem por falta de coordenação.
            </span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#51565F]">
            A força de uma campanha não está só no horário eleitoral. Está no que
            acontece todos os dias entre apoiadores, lideranças e território — e
            esse é exatamente o ponto cego da maioria dos QGs.
          </p>
        </div>

        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
          {PAINS.map((p, i) => (
            <div
              key={p}
              data-testid={`pain-card-${i}`}
              className="group rounded-2xl border border-[#1B1D1D]/8 bg-white p-6 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#D94A0A]/40"
            >
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-[#D94A0A]/10 text-[#D94A0A] transition-colors group-hover:bg-[#D94A0A] group-hover:text-white">
                <AlertTriangle size={18} />
              </div>
              <p className="mt-4 text-base text-[#1B1D1D] leading-snug">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  3 — Pillars (HOW)                                            */
/* ============================================================= */
const PILLARS = [
  {
    icon: Users,
    title: "Mobilização Social",
    desc: "Base cadastrada, segmentada e com missões claras — apoiador vira agente, não nome em planilha.",
    accent: "#208BFF",
  },
  {
    icon: Radio,
    title: "Influência nas Redes",
    desc: "Apoiadores atuando como mídia dirigida. Engagement Media político — não apenas link solto no grupo.",
    accent: "#7E20FF",
  },
  {
    icon: BarChart3,
    title: "Pesquisas em Tempo Real",
    desc: "Enquetes e surveys internos informando a estratégia diariamente, além dos institutos formais.",
    accent: "#FA9F22",
  },
  {
    icon: Heart,
    title: "Fidelidade da Base",
    desc: "Engajamento medido por pessoa, região e grupo. Você descobre quem entrega antes da urna.",
    accent: "#3ED383",
  },
  {
    icon: Brain,
    title: "IA e Relatórios Contínuos",
    desc: "Volumes massivos processados para revelar narrativas que tracionam, humores regionais e sinais de alerta.",
    accent: "#1B1D1D",
  },
];

export function PillarsSection() {
  return (
    <section
      id="pilares"
      data-testid="pillars-section"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <SectionEyebrow>Como a RSM muda o jogo</SectionEyebrow>
          <h2
            data-testid="pillars-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Cinco pilares que conectam estratégia, base e narrativa.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F]">
            Não é uma ferramenta — é um sistema operacional para campanhas que
            entendem que a eleição começa muito antes do horário eleitoral.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            // Bento: first card wider, last spans 2
            const span =
              i === 0
                ? "lg:col-span-3"
                : i === 4
                ? "lg:col-span-3"
                : "lg:col-span-2";
            return (
              <article
                key={pillar.title}
                data-testid={`pillar-card-${i}`}
                className={`${span} group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15`}
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${pillar.accent}15`,
                    color: pillar.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#1B1D1D]">
                  {pillar.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {pillar.desc}
                </p>
                <span
                  className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                  style={{ background: pillar.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  4 — "Tenha seu próprio instituto de pesquisas"               */
/* ============================================================= */
export function InstituteSection() {
  return (
    <section
      data-testid="institute-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <SectionEyebrow>Inteligência de campo</SectionEyebrow>
          <h2
            data-testid="institute-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Tenha seu próprio instituto de pesquisas.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F]">
            A RSM permite que sua campanha planeje, execute e consolide pesquisas
            com a sua própria base — sem depender exclusivamente de institutos
            externos.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Veja como diferentes segmentos da base reagem a mensagens e propostas.",
              "Compare reações por região, faixa etária e grupo.",
              "Complemente pesquisas registradas com inteligência diária de campo.",
            ].map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-[15px] leading-relaxed text-[#1B1D1D]"
              >
                <CheckCircle2
                  size={18}
                  className="mt-0.5 text-[#208BFF] flex-shrink-0"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-6 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between border-b border-[#1B1D1D]/8 pb-4">
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#7D8188]">
                Survey · "Proposta X"
              </span>
              <span className="text-xs font-semibold text-[#208BFF]">
                4.218 respostas
              </span>
            </div>
            <div className="mt-6 space-y-5">
              {[
                { label: "Apoia totalmente", v: 62, c: "#3ED383" },
                { label: "Apoia parcialmente", v: 24, c: "#208BFF" },
                { label: "Indeciso", v: 9, c: "#FA9F22" },
                { label: "Discorda", v: 5, c: "#D94A0A" },
              ].map((row) => (
                <div key={row.label}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-[#1B1D1D] font-medium">
                      {row.label}
                    </span>
                    <span className="font-display font-semibold text-[#1B1D1D]">
                      {row.v}%
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 rounded-full bg-[#F5FAFB] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${row.v}%`, background: row.c }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  5 — "Sua base é fiel?"                                       */
/* ============================================================= */
export function FidelitySection() {
  return (
    <section
      data-testid="fidelity-section"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -top-32 -right-20 h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-7">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Fidelidade da base
          </span>
          <h2
            data-testid="fidelity-headline"
            className="mt-4 font-display text-5xl sm:text-7xl font-bold tracking-tighter leading-[0.92] text-[#F5FAFB]"
          >
            Sua base
            <br />
            é <span className="text-[#7E20FF]">fiel?</span>
          </h2>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#F5FAFB]/75">
            Promessa de palanque é fácil. Entrega é outra história. Na RSM, cada
            apoiador tem missões registradas — e o QG vê quem entrega, quem está
            ausente e onde estão os buracos da rede.
          </p>
        </div>

        <div className="lg:col-span-5 space-y-3 mt-6 lg:mt-0">
          {[
            {
              label: "Núcleo Sul · 142 apoiadores",
              status: "Acima da meta",
              pct: 92,
              color: "#3ED383",
            },
            {
              label: "Núcleo Centro · 87 apoiadores",
              status: "Atenção",
              pct: 58,
              color: "#FA9F22",
            },
            {
              label: "Núcleo Norte · 64 apoiadores",
              status: "Risco — base inativa",
              pct: 24,
              color: "#D94A0A",
            },
          ].map((row, i) => (
            <div
              key={row.label}
              data-testid={`fidelity-row-${i}`}
              className="rounded-xl border border-white/8 bg-white/5 backdrop-blur p-5"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium text-[#F5FAFB]">
                  {row.label}
                </span>
                <span
                  className="text-xs font-semibold"
                  style={{ color: row.color }}
                >
                  {row.status}
                </span>
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${row.pct}%`, background: row.color }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  6 — Personas                                                 */
/* ============================================================= */
const PERSONAS = [
  {
    title: "Partidos & Diretórios",
    role: "Visão de legenda",
    bullets: [
      "Acompanhe todas as campanhas da legenda em um só painel.",
      "Veja engajamento por estado, microrregião e segmento.",
      "Sincronize narrativas entre majoritários e proporcionais.",
    ],
    accent: "#208BFF",
  },
  {
    title: "Candidatos",
    role: "Quem disputa o voto",
    bullets: [
      "Cadastre e acompanhe a sua própria base.",
      "Veja engajamento em ações digitais e presenciais.",
      "Massifique conteúdo orgânico com a sua rede.",
    ],
    accent: "#7E20FF",
  },
  {
    title: "Coordenadores",
    role: "Quem comanda o QG",
    bullets: [
      "Dashboards da evolução da rede e das campanhas.",
      "Resultados de missões e pesquisas no mesmo lugar.",
      "Indicadores acionáveis para ajustar a rota.",
    ],
    accent: "#FA9F22",
  },
  {
    title: "Marqueteiros",
    role: "Quem desenha a comunicação",
    bullets: [
      "Teste mensagens, peças e propostas com a base.",
      "Leia rapidamente o termômetro das redes e da rua.",
      "Ajuste comunicação com menos achismo.",
    ],
    accent: "#3ED383",
  },
];

export function PersonasSection() {
  return (
    <section
      id="personas"
      data-testid="personas-section"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <SectionEyebrow>Para quem é</SectionEyebrow>
          <h2
            data-testid="personas-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Um painel diferente para cada cabeça do QG.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {PERSONAS.map((p, i) => (
            <article
              key={p.title}
              data-testid={`persona-card-${i}`}
              className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
            >
              <span
                className="text-[10px] font-bold tracking-[0.22em] uppercase"
                style={{ color: p.accent }}
              >
                {p.role}
              </span>
              <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight text-[#1B1D1D]">
                {p.title}
              </h3>
              <ul className="mt-5 space-y-3 flex-1">
                {p.bullets.map((b, j) => (
                  <li
                    key={j}
                    className="flex items-start gap-2.5 text-sm leading-relaxed text-[#51565F]"
                  >
                    <CheckCircle2
                      size={16}
                      className="mt-0.5 flex-shrink-0"
                      style={{ color: p.accent }}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div
                className="mt-6 h-1 w-10 rounded-full transition-all duration-300 group-hover:w-16"
                style={{ background: p.accent }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  7 — Legal                                                    */
/* ============================================================= */
export function LegalSection() {
  return (
    <section
      data-testid="legal-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <SectionEyebrow>Segurança jurídica</SectionEyebrow>
          <h2
            data-testid="legal-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Organizar a base — dentro das regras.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            A operação da RSM Politics conta com escritório de direito eleitoral
            especializado, atuando de forma consultiva e no contencioso quando
            necessário. Nosso compromisso é organizar a sua base{" "}
            <span className="text-[#1B1D1D] font-medium">
              dentro das regras
            </span>{" "}
            — não burlar o sistema.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-8 shadow-sm">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#1B1D1D] text-white">
              <Scale size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
              Suporte jurídico-eleitoral embarcado
            </h3>
            <div className="mt-5 space-y-3 text-[15px] text-[#51565F]">
              <p>· Análise consultiva de ações de mobilização e mídia.</p>
              <p>· Contencioso eleitoral quando necessário.</p>
              <p>· Acompanhamento de prazos e obrigações de campanha.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  8 — Nascida em Brasília                                      */
/* ============================================================= */
export function BrasiliaSection() {
  return (
    <section
      data-testid="brasilia-section"
      className="relative isolate py-24 sm:py-32 overflow-hidden text-[#F5FAFB] border-y border-[#1B1D1D]/8"
    >
      {/* Layer 1 — full-bleed architecture photo */}
      <img
        src="https://images.pexels.com/photos/2449231/pexels-photo-2449231.jpeg?auto=compress&cs=tinysrgb&w=2400"
        alt="Arquitetura futurista de Brasília"
        className="absolute inset-0 z-0 h-full w-full object-cover"
        style={{ filter: "contrast(1.05) brightness(1.0) saturate(0.4)" }}
      />

      {/* Layer 2 — blue duotone tint */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(11,18,32,0.35) 0%, rgba(32,139,255,0.30) 50%, rgba(11,18,32,0.55) 100%)",
        }}
      />

      {/* Layer 3 — content readability gradient (left to right) */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(90deg, rgba(11,18,32,0.85) 0%, rgba(11,18,32,0.55) 38%, rgba(11,18,32,0.08) 70%, rgba(11,18,32,0.0) 100%)",
        }}
      />

      {/* Layer 4 — subtle dot grid texture */}
      <div
        className="absolute inset-0 z-10 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-20 mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] backdrop-blur px-3.5 py-1.5 text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            <MapPin size={14} /> Brasília · DF
          </div>
          <h2
            data-testid="brasilia-headline"
            className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] text-[#F5FAFB]"
          >
            Nascida em <span className="text-[#208BFF]">Brasília</span> —
            <br className="hidden sm:block" /> não é coincidência.
          </h2>
          <p className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#F5FAFB]/75">
            A RSM Politics foi desenvolvida no coração político do país.
            Conhecemos a realidade do QG, a logística de reuniões presenciais e o
            ritmo das campanhas de alto impacto — governos, majoritários e
            proporcionais relevantes.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg">
            {[
              { k: "DF", v: "Sede" },
              { k: "100%", v: "Foco político" },
              { k: "24/7", v: "Período eleitoral" },
            ].map((s, i) => (
              <div
                key={s.k}
                className="rounded-2xl border border-white/12 bg-white/[0.06] backdrop-blur-md p-5"
              >
                <div className="font-display text-2xl sm:text-3xl font-bold text-[#F5FAFB]">
                  {s.k}
                </div>
                <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#F5FAFB]/55 mt-1 font-semibold">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column — subtle architectural caption */}
        <div className="lg:col-span-5 lg:pl-12 hidden lg:block">
          <div className="rounded-2xl border border-white/15 bg-[#0B1220]/55 backdrop-blur-md px-6 py-5 max-w-sm">
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#208BFF]">
              15°47′S · 47°52′W
            </span>
            <p className="mt-3 text-sm leading-relaxed text-[#F5FAFB]/85">
              Construída para o poder. Operada com a precisão de quem entende a
              gramática das instituições brasileiras.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  9 — Journey                                                  */
/* ============================================================= */
const STEPS = [
  {
    n: "01",
    title: "Diagnóstico com o QG",
    desc: "Mergulho na sua operação atual, mapeamento de base, lideranças e canais.",
  },
  {
    n: "02",
    title: "Desenho da base e missões",
    desc: "Segmentação inicial e definição das primeiras missões digitais e presenciais.",
  },
  {
    n: "03",
    title: "Lançamento da rede",
    desc: "Apoiadores entram na plataforma, recebem suas missões e começam a operar.",
  },
  {
    n: "04",
    title: "Acompanhamento diário",
    desc: "Dashboards, pesquisas e leituras de IA para ajustar narrativa em tempo real.",
  },
];

export function JourneySection() {
  return (
    <section
      data-testid="journey-section"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <SectionEyebrow tone="purple">Como funciona na prática</SectionEyebrow>
          <h2
            data-testid="journey-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Em quatro movimentos — do QG à urna.
          </h2>
        </div>

        <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((step, i) => (
            <li
              key={step.n}
              data-testid={`journey-step-${i}`}
              className="relative rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#208BFF]/40"
            >
              <span className="font-mono text-sm font-semibold text-[#7E20FF]">
                {step.n}
              </span>
              <h3 className="mt-3 font-display text-xl font-semibold text-[#1B1D1D]">
                {step.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#51565F]">
                {step.desc}
              </p>
              {i < STEPS.length - 1 && (
                <span className="hidden lg:block absolute top-1/2 right-[-14px] -translate-y-1/2 text-[#1B1D1D]/15">
                  <ArrowRight size={20} />
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-14 text-center">
          <a
            href="#contato"
            data-testid="journey-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1B1D1D] px-7 py-4 text-base font-semibold text-white transition-all duration-300 ease-out hover:bg-[#208BFF] hover:-translate-y-0.5"
          >
            Quero minha base na RSM
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  10 — FAQ                                                     */
/* ============================================================= */
const FAQS = [
  {
    q: "Quanto tempo leva para implantar?",
    a: "O diagnóstico inicial é feito em até duas semanas. A operação pode começar a rodar com a base inicial em até 30 dias, dependendo do tamanho e da maturidade da campanha.",
  },
  {
    q: "Qual o tamanho mínimo da base?",
    a: "A plataforma é eficiente desde grupos iniciais de centenas de apoiadores até bases que ultrapassam dezenas de milhares. O ganho de inteligência cresce com o volume e a qualidade do cadastro.",
  },
  {
    q: "Como funciona o pacote de preço?",
    a: "Trabalhamos com um pacote RSM Politics com modelo definido internamente e adequado ao porte da campanha (governo, majoritário, proporcional ou estrutura partidária). O escopo é apresentado em reunião com o QG.",
  },
  {
    q: "Há risco jurídico para a campanha?",
    a: "Toda a operação é desenhada em conjunto com escritório de direito eleitoral especializado, atuando de forma consultiva e — quando necessário — no contencioso. Organizamos sua base dentro das regras.",
  },
  {
    q: "A plataforma substitui TV, rádio e redes sociais?",
    a: "Não. A RSM convive e potencializa esses canais — transforma sua base em mídia dirigida (Engagement Media político) e te dá inteligência para decidir melhor o que veicular nos meios tradicionais.",
  },
];

export function FAQSection() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center">
          <SectionEyebrow>Perguntas frequentes</SectionEyebrow>
          <h2
            data-testid="faq-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Tirando as principais dúvidas dos QGs.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 divide-y divide-[#1B1D1D]/10 rounded-2xl border border-[#1B1D1D]/10 bg-white"
          data-testid="faq-accordion"
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-b-0 px-6"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#1B1D1D] hover:text-[#208BFF] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#51565F] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
